// [access-gate]
import { useState } from 'react'
import { useSelector } from 'react-redux'
import type { RootState } from '../../store'
import { ExternalLink, KeyRound } from 'lucide-react'
import { useAnswerAccessRequestMutation, useGetProjectAccessQuery, useSaveVercelAccessMutation } from '../../services/projectDataApi'
import { accessGuidance } from '../../lib/access-gate/guidance'
import { accessTargetOrigin, distinctAccessRequests } from '../../lib/access-gate/requests'
import { PhoneNumberSchema, type AccessResume, type ProjectAccessRequest } from '../../schemas/access-gate'
import './chat.css'

const plural = (count: number, noun: string) => `${count} ${noun}${count === 1 ? '' : 's'}`

function resumeSummary(resumed: AccessResume | null): string {
  const queued = [resumed?.journeys && plural(resumed.journeys, 'journey run'), resumed?.explorations && plural(resumed.explorations, 'exploration')].filter(Boolean)
  return [queued.length ? `Re-queued ${queued.join(' and ')}.` : 'Nothing needed re-queueing; the next run rechecks access.',
    resumed?.pr_checks ? `Re-run the PR check for ${plural(resumed.pr_checks, 'blocked PR run')}.` : ''].filter(Boolean).join(' ')
}

function AccessRequestCard({ projectId, request, onResolved }: { projectId: string; request: ProjectAccessRequest; onResolved: (status: string) => void }) {
  const guidance = accessGuidance(request.provider, request)
  const standing = request.handoff === 'notify'
  const [value, setValue] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [answer, mutation] = useAnswerAccessRequestMutation()
  return (
    <form className="chat-form" data-testid="chat-access-request" onSubmit={async event => {
      event.preventDefault(); setError(null)
      if (guidance.input === 'phone' && !PhoneNumberSchema.safeParse(value).success) {
        setError('Enter a complete phone number in the format requested by the blocked page.'); return
      }
      try {
        const result = await answer({ projectId, requestId: request.id, value: guidance.input === 'retry' ? '' : value.trim() }).unwrap()
        setValue(''); onResolved(standing || result.resumed ? resumeSummary(result.resumed) : 'Access response sent. QA is rechecking this run.')
      } catch { setError(standing ? 'Could not resolve this request. Only the project owner or an admin can respond.' : 'Could not answer this request. Only the project owner or an admin can respond, and the run must still be waiting.') }
      finally { mutation.reset() }
    }}>
      <div className="chat-form__head"><KeyRound size={15} aria-hidden /><span className="chat-form__title">{standing ? `QA is blocked: ${guidance.title}` : guidance.title}</span></div>
      <p className="chat-form__hint">{request.detail}</p>
      <p className="chat-form__hint">Blocked page: <a href={request.url} target="_blank" rel="noreferrer">{new URL(request.url).host}</a></p>
      <p className="chat-form__hint">{guidance.instructions}</p>
      {guidance.docs && <p className="chat-form__hint">
        <a className="chat-form__guide-link" href={guidance.docs} target="_blank" rel="noreferrer">
          {request.provider === 'cloudflare' || request.provider === 'turnstile' ? 'How to disable Cloudflare bot protection' : request.provider === 'vercel' ? 'Set up Vercel preview access' : 'Open setup guide'} <ExternalLink size={14} aria-hidden />
        </a>
      </p>}
      <p className="chat-form__hint">{standing
        ? `${plural(request.blocked_runs, 'run')} on ${accessTargetOrigin(request)} ended blocked. Retrying re-queues blocked journeys and explorations; PR checks need a new run from the PR.`
        : `Waiting until ${new Date(request.expires_at).toLocaleTimeString()}.${guidance.input === 'otp' ? ' Codes expire quickly; submit the current code.' : ''}`}</p>
      {guidance.input !== 'retry' && <label className="chat-form__hint">
        {guidance.input === 'otp' ? 'Verification code' : guidance.input === 'phone' ? 'Phone number' : 'Vercel automation bypass secret'}
        <input className="chat-form__input fs-mask rr-mask" data-private="true" type="password" autoComplete="off"
          inputMode={guidance.input === 'phone' ? 'tel' : guidance.input === 'otp' && request.verification_channel !== 'email' ? 'numeric' : 'text'} maxLength={guidance.input === 'otp' ? 12 : guidance.input === 'phone' ? 32 : 1024}
          value={value} onChange={event => setValue(event.target.value)} required disabled={mutation.isLoading} />
      </label>}
      {error && <p className="pchat__error" role="alert">{error}</p>}
      <div className="chat-form__actions"><button type="submit" className="chat-form__submit" disabled={mutation.isLoading}>
        {mutation.isLoading ? 'Sending…' : guidance.input === 'retry' ? 'Protection updated — retry' : guidance.input === 'otp' ? 'Use code in this run' : guidance.input === 'phone' ? 'Use phone number in this run' : 'Save secret and retry'}
      </button></div>
    </form>
  )
}

export default function ChatAccessRequests({ projectId }: { projectId: string }) {
  const user = useSelector((state: RootState) => state.auth.user)
  // Only signed-in owners/admins can answer access requests, so without a session the poll can
  // never deliver data: every request 401s ("Sign in required"), and RTK Query re-issues it every
  // 5s for the page's whole lifetime — 7 doomed requests in the session behind issue #5714. Skip
  // while signed out; the same rule usePlaywrightTestsEnabled applies to its poll.
  const query = useGetProjectAccessQuery(projectId, { skip: !user, pollingInterval: 5000, skipPollingIfUnfocused: true })
  // The latest result outlives its card, which leaves the list on the next refetch.
  const [resolved, setResolved] = useState<{ id: string; status: string } | null>(null)
  const requests = distinctAccessRequests(query.currentData?.requests ?? []).filter(request => request.id !== resolved?.id)
  return <>
    {requests.map(request => <AccessRequestCard key={request.id} projectId={projectId} request={request} onResolved={status => setResolved({ id: request.id, status })} />)}
    {resolved && <p role="status">{resolved.status}</p>}
  </>
}

function VercelAccessEditor({ projectId, canEdit }: { projectId: string; canEdit: boolean }) {
  const query = useGetProjectAccessQuery(projectId)
  const [editing, setEditing] = useState(false)
  const [secret, setSecret] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [resumed, setResumed] = useState<string | null>(null)
  const [save, mutation] = useSaveVercelAccessMutation()
  const guidance = accessGuidance('vercel')
  const write = async (value: string | null) => {
    setError(null); setResumed(null)
    try {
      const result = await save({ projectId, secret: value }).unwrap(); setSecret(''); setEditing(false)
      if (result.resumed) setResumed(resumeSummary(result.resumed))
    }
    catch { setError('Could not save deployment access. Try again.') }
    finally { mutation.reset() }
  }
  return <div className="chat-form" data-testid="vercel-deployment-access">
    <div className="chat-form__head"><KeyRound size={15} aria-hidden /><span className="chat-form__title">Vercel preview access</span></div>
    <p className="chat-form__hint">{query.isFetching ? 'Loading…' : query.isError ? 'Access settings unavailable.' : query.currentData?.vercel_configured ? 'Bypass secret configured.' : 'No bypass secret configured.'}</p>
    <p className="chat-form__hint">{guidance.instructions}</p>
    <p className="chat-form__hint"><a className="chat-form__guide-link" href={guidance.docs!} target="_blank" rel="noreferrer">Set up Vercel preview access <ExternalLink size={14} aria-hidden /></a></p>
    <p className="chat-form__hint">Vercel secrets grant access across that Vercel project. Use a dedicated secret you can revoke. App sign-in credentials are configured separately.</p>
    {editing && <form onSubmit={event => { event.preventDefault(); void write(secret.trim()) }}>
      <label className="chat-form__hint">Automation bypass secret
        <input className="chat-form__input fs-mask rr-mask" data-private="true" type="password" autoComplete="off" value={secret} onChange={event => setSecret(event.target.value)} required maxLength={1024} disabled={mutation.isLoading} />
      </label>
      <div className="chat-form__actions">
        <button className="chat-form__submit" type="submit" disabled={mutation.isLoading}>{mutation.isLoading ? 'Saving…' : 'Save secret'}</button>
        <button className="chat-form__cancel" type="button" disabled={mutation.isLoading} onClick={() => { setEditing(false); setSecret(''); setError(null) }}>Cancel</button>
      </div>
    </form>}
    {canEdit && !editing && !query.isError && <div className="chat-form__actions">
      <button type="button" className="chat-form__submit" disabled={query.isLoading || mutation.isLoading} onClick={() => setEditing(true)}>{query.currentData?.vercel_configured ? 'Replace secret' : 'Set up bypass'}</button>
      {query.currentData?.vercel_configured && <button type="button" className="chat-form__cancel" disabled={mutation.isLoading} onClick={() => { void write(null) }}>Remove secret</button>}
    </div>}
    {resumed && <p className="chat-form__hint" role="status">{resumed}</p>}
    {error && <p className="pchat__error" role="alert">{error}</p>}
  </div>
}

export function VercelAccessSettings(props: { projectId: string; canEdit: boolean }) {
  return <VercelAccessEditor key={props.projectId} {...props} />
}
